"use client";

import React from "react";
import { Box, Factory, Layers, Globe, Store, UserCheck } from "lucide-react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const manufacturersNodes = [{
  icon: Box,
  label: "DISCOVERY",
  position: "top"
}, {
  icon: Factory,
  label: "STRATEGY",
  position: "bottom"
}, {
  icon: Layers,
  label: "VALIDATION",
  position: "top"
}];
const demandNodes = [{
  icon: Globe,
  label: "EXECUTION SUPPORT",
  position: "bottom"
}, {
  icon: Store,
  label: "PARTNERSHIP (SELECTIVE)",
  position: "top"
}, {
  icon: UserCheck,
  label: "End Consumer",
  position: "bottom"
}];
export default function TheSupplyChainNexus() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const noiseTexture = `data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E`;
  return <section id="the-supply-chain-nexus" className="relative w-full py-24 md:py-32 bg-background overflow-hidden" ref={ref}>
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-multiply z-0" style={{
      backgroundImage: `url("${noiseTexture}")`
    }} aria-hidden="true" />

      {}
      <style dangerouslySetInnerHTML={{
      __html: `
          @keyframes nexus-pulse {
            0% { left: 0%; opacity: 0; transform: translateX(-100%); }
            10% { opacity: 1; }
            90% { opacity: 1; }
            100% { left: 100%; opacity: 0; transform: translateX(0); }
          }
          .animate-nexus-pulse {
            animation: nexus-pulse 4s ease-in-out infinite;
          }
        `
    }} />

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div className="text-center mb-16 md:mb-24">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-foreground mb-4 tracking-tight">How We Work</h2>
          <p className="text-muted-foreground tracking-[0.2em] uppercase text-xs md:text-sm font-medium">Advisory when expertise is needed. Partnership when opportunity justifies it.</p>
        </div>

        {}
        <ScrollArea className="w-full pb-8" type="always">
          <div className="min-w-[1024px] w-full relative h-[450px] grid grid-cols-7 items-center mx-auto">
            
            {}
            <div className="absolute left-0 right-0 h-px bg-border top-1/2 -translate-y-1/2 z-0" />

            {}
            <div className={`absolute h-[2px] w-48 bg-gradient-to-r from-transparent via-primary to-transparent top-1/2 -translate-y-1/2 z-10 ${isVisible ? "animate-nexus-pulse" : "opacity-0"}`} />

            {}
            {manufacturersNodes.map((node, i) => <div key={`mfg-${i}`} data-index={i} className="relative flex justify-center w-full h-full items-center group z-20">
                {}
                <div className="w-2.5 h-2.5 rounded-full bg-border group-hover:bg-primary transition-colors duration-300 absolute z-10" />
                
                {}
                <div className={`absolute w-px bg-border group-hover:bg-primary/40 transition-colors duration-300 ${node.position === "top" ? "bottom-1/2 h-20" : "top-1/2 h-20"}`} />
                
                {}
                <div className={`absolute flex flex-col items-center gap-4 transition-all duration-500 ${node.position === "top" ? "bottom-[calc(50%+5rem)] group-hover:-translate-y-2" : "top-[calc(50%+5rem)] group-hover:translate-y-2"}`}>
                  <div className="w-14 h-14 rounded-full border border-border bg-background flex items-center justify-center group-hover:border-primary group-hover:shadow-md transition-all duration-300 relative z-20">
                    <node.icon className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors duration-300" strokeWidth={1.5} />
                  </div>
                  <span className="text-[11px] uppercase tracking-widest text-muted-foreground whitespace-nowrap group-hover:text-foreground transition-colors duration-300 font-medium">
                    {node.label}
                  </span>
                </div>
              </div>)}

            {}
            <div className="relative flex justify-center items-center w-full h-full z-30">
              <div className="bg-background px-8 py-8 border border-border shadow-sm flex flex-col items-center justify-center relative group hover:shadow-md transition-shadow duration-500">
                {}
                <div className="flex items-center text-5xl font-bold text-primary tracking-tighter" style={{
                textShadow: '1px 1px 2px rgba(0,0,0,0.05), -1px -1px 1px rgba(255,255,255,0.8)'
              }}>
                  <span className="z-10">T</span>
                  <span className="-ml-2 z-20">C</span>
                  <span className="-ml-2 z-30">C</span>
                </div>
                {}
                <div className="mt-5 text-[9px] tracking-[0.25em] text-secondary-foreground font-medium whitespace-nowrap">
                  THE CAREFUL CONSULTING
                </div>
                {}
                <div className="absolute bottom-2 right-2 text-secondary-foreground/40 text-[10px] select-none">
                  
                </div>
              </div>
            </div>

            {}
            {demandNodes.map((node, i) => <div key={`dem-${i}`} data-index={i} className="relative flex justify-center w-full h-full items-center group z-20">
                {}
                <div className="w-2.5 h-2.5 rounded-full bg-border group-hover:bg-primary transition-colors duration-300 absolute z-10" />
                
                {}
                <div className={`absolute w-px bg-border group-hover:bg-primary/40 transition-colors duration-300 ${node.position === "top" ? "bottom-1/2 h-20" : "top-1/2 h-20"}`} />
                
                {}
                <div className={`absolute flex flex-col items-center gap-4 transition-all duration-500 ${node.position === "top" ? "bottom-[calc(50%+5rem)] group-hover:-translate-y-2" : "top-[calc(50%+5rem)] group-hover:translate-y-2"}`}>
                  <div className="w-14 h-14 rounded-full border border-border bg-background flex items-center justify-center group-hover:border-primary group-hover:shadow-md transition-all duration-300 relative z-20">
                    <node.icon className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors duration-300" strokeWidth={1.5} />
                  </div>
                  <span className="text-[11px] uppercase tracking-widest text-muted-foreground whitespace-nowrap group-hover:text-foreground transition-colors duration-300 font-medium">
                    {node.label}
                  </span>
                </div>
              </div>)}

          </div>
          <ScrollBar orientation="horizontal" className="opacity-50 hover:opacity-100 transition-opacity" />
        </ScrollArea>
      </div>
    </section>;
}